import { LoadingOutlined } from "@ant-design/icons";
import { Storage } from "aws-amplify";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FolderImage } from "../../assets/svgs";
import { FaTimes } from "react-icons/fa";

import { ImageFile, UserOrder } from "../../interfaces";
import { getFileSizeInMb } from "../../utils";
import useHandleS3 from "../../hooks/useHandleS3";
import useOrder from "../../hooks/useOrder";

type Props = {
  data?: UserOrder;
  files: ImageFile[];
};

type TAttachedFile = ImageFile & {
  size?: number;
};

const AdminAttachment: React.FC<Props> = ({ data, files }) => {
  const [downloadingFiles, setDownloadingFiles] = useState<{
    [key: string]: string;
  }>({});
  const [attachedFiles, setAttachedFiles] = useState<TAttachedFile[]>([]);
  const [, setDeliveryNoteLoading] = useState<boolean>(false);
  const { listImagesByOrderId } = useHandleS3({
    data,
  });
  const { t } = useTranslation();
  const { handleOrderUpdate } = useOrder();

  const handleFileDownload = async (key: string, signedUrl: string) => {
    const element = document.createElement("a");
    element.style.display = "none";
    element.setAttribute("href", signedUrl);
    element.setAttribute("target", "_blank");
    element.setAttribute("download", key);
    document.body.appendChild(element);
    element.click();
    element.remove();
  };

  const handleRemoveNote = async (index: number, item: TAttachedFile) => {
    if (!data) return;
    const updatedNotes = attachedFiles.filter((_, idx) => idx !== index);
    let removeFile: ImageFile | undefined;
    setAttachedFiles(updatedNotes);
    const filteredFile = data?.files.filter((note) => {
      if (note.key === item.key) {
        removeFile = note;
        return false;
      }
      return true;
    });
    data.files = filteredFile;
    await handleOrderUpdate({ id: data?.id, files: data?.files });
    toast.success(t("positionImageRemoved"));
    if (removeFile) {
      Storage.remove(removeFile.key);
    }
  };

  const fetchingPositionImage = async () => {
    try {
      setDeliveryNoteLoading(true);
      const file =
        files?.map(async (item) => {
          try {
            const fetchedFiles = listImagesByOrderId.find(
              (val) => val.key === item.key
            );
            if (fetchedFiles && fetchedFiles.key && fetchedFiles.size) {
              return {
                key: item.key,
                name: item.name,
                size: Number(getFileSizeInMb(fetchedFiles.size || 0)),
              };
            }
          } catch (error) {
            if (error && (error as Error).message) {
              toast.error((error as Error).message);
            } else {
              toast.error("We are unable to load image details contact admin.");
            }
            return undefined;
          }
        }) || [];
      const response = await Promise.all(file);
      setAttachedFiles((response as TAttachedFile[]).filter((f) => f));
    } catch (error) {
      if (error && (error as Error).message) {
        toast.error((error as Error).message);
      } else {
        toast.error("We are unable to fetch delivery notes.");
      }
    } finally {
      setDeliveryNoteLoading(false);
    }
  };

  React.useEffect(() => {
    if (!files) return;
    fetchingPositionImage();
  }, [files, listImagesByOrderId]);

  const handleDownload = async ({ key }: ImageFile) => {
    setDownloadingFiles({ ...downloadingFiles, [key]: key });
    const signedUrl = await Storage.get(key);
    const { [key]: removed, ...rest } = downloadingFiles;
    setTimeout(() => {
      setDownloadingFiles({ ...rest });
      handleFileDownload(key, signedUrl);
    }, 100);
  };

  return (
    <>
      <div>
        {attachedFiles.length === 0 && (
          <span className="f-16 pl-8 f-w-700">{t("No file")}</span>
        )}

        {attachedFiles.map((file, index) => (
          <div className="card-auto p-16 card-container my-10 grid row-space-between gap-8">
            <div>
              <FolderImage />
            </div>
            <div className="image-files__file">
              <span onClick={() => handleDownload(file)}>
                {downloadingFiles[file.key] && (
                  <LoadingOutlined style={{ marginRight: 5 }} />
                )}
                <div className="row pl-8 row-column">
                  <h4 className="overflow-wrap-anywhere">{file.name}</h4>
                  <p className="f-16">{file.size?.toFixed(3)}MB</p>
                </div>
              </span>
              <hr />
            </div>
            <FaTimes onClick={() => handleRemoveNote(index, file)} />
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminAttachment;
